import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import type { DeadlineFormInterface } from '@zaoblako/shared/common/dtos/deadline/deadline-form.dto';
import type { PlanningUnitFormInterface } from '@zaoblako/shared/common/dtos/planning-unit/planning-unit-form.dto';
import { DeadlineTypeEnum } from '@zaoblako/shared/common/enums/deadline.type.enum';
import { TemplateEntityPeriodicityEnum } from '@zaoblako/shared/common/enums/template.entity.periodicity.enum';
import type { ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import type { HelpdeskTaskFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-find.dto';
import type { HelpdeskTaskTypeFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/task-type/helpdesk-task-type-find.dto';
import type { HelpdeskTaskTemplateFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/template/helpdesk-task-template-find.dto';
import {
  HelpdeskTaskTemplateFormDto,
  type HelpdeskTaskTemplateFormInterface
} from '@zaoblako/shared/modules/helpdesk/dto/template/helpdesk.task.template.form.dto';
import { HelpdeskTaskPriorityEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk.task.priority.enum';
import type { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { ClientService } from '@zaoblako/web/core/services/client/client.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { HelpDeskSettingsService } from '@zaoblako/web/core/services/helpdesk/help-desk-settings.service';
import { HelpDeskTaskTemplateService } from '@zaoblako/web/core/services/helpdesk/help-desk-task-template.service';
import { HelpDeskTaskTypeService } from '@zaoblako/web/core/services/helpdesk/help-desk-task-type.service';
import { HelpDeskTaskService } from '@zaoblako/web/core/services/helpdesk/help-desk-task.service';
import { UserGroupService } from '@zaoblako/web/core/services/security/user-group.service';
import { UserService } from '@zaoblako/web/core/services/security/user.service';
import { CreatedTasksWidgetComponent } from '@zaoblako/web/modules/help-desk/dumbs/helpdesk-template-form/created-tasks-widget/created-tasks-widget.component';
import { TypeFormEnum } from '@zaoblako/web/shared/components/form-sidebar/enums/type.form.enum';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { DealAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/deal-auto-complete/deal-auto-complete.component';
import { ProjectAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/project-auto-complete/project-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { ExternalUserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/client/external-user-single-select/external-user-single-select.component';
import { FilialSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-single-select/filial-single-select.component';
import { HdTaskTypeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/hd-task-type-single-select/hd-task-type-single-select.component';
import { MasterMilestoneSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/master-milestone-single-select/master-milestone-single-select.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { UnitSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/unit-single-select/unit-single-select.component';
import { UserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-multi-select/user-multi-select.component';
import { UserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-single-select/user-single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { PlanningDeadlineFormComponent } from '@zaoblako/web/shared/dumbs/planning-deadline-form/planning-deadline-form.component';
import { PlanningPeriodicityCardComponent } from '@zaoblako/web/shared/dumbs/planning-periodicity-card/planning-periodicity-card.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { TemplateSubjectEditorComponent } from '@zaoblako/web/shared/dumbs/template-subject-editor/template-subject-editor.component';
import { HelpdeskTaskPriorityNamePipe } from '@zaoblako/web/shared/pipes/helpdesk-task-priority-name.pipe';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import * as moment from 'moment-timezone';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { DialogService } from 'primeng/dynamicdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { forkJoin, Observable, of, Subscription, switchMap, tap } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-helpdesk-template-form',
  templateUrl: './helpdesk-template-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    CheckboxModule,
    FormsModule,
    SingleSelectComponent,
    TemplateSubjectEditorComponent,
    InputNumberModule,
    HdTaskTypeSingleSelectComponent,
    DividerModule,
    PlanningDeadlineFormComponent,
    PlanningPeriodicityCardComponent,
    ExternalUserSingleSelectComponent,
    ButtonModule,
    MultiSelectComponent,
    HelpdeskTaskPriorityNamePipe,
    CreatedTasksWidgetComponent,
    SpinnerComponent,
    DealAutoCompleteComponent,
    ProjectAutoCompleteComponent,
    UnitSingleSelectComponent,
    FilialSingleSelectComponent,
    UserSingleSelectComponent,
    UserMultiSelectComponent,
    MasterMilestoneSingleSelectComponent,
    DatepickerComponent
  ],
  providers: [DialogService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HelpdeskTemplateFormComponent
  extends BaseFormComponent<HelpdeskTaskTemplateFormInterface>
  implements OnInit, OnChanges, OnDestroy
{
  @Input() public readonly index: number;
  public helpdeskTemplate = <HelpdeskTaskTemplateFormInterface>{};
  public dto = HelpdeskTaskTemplateFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;
  public engine: 'class-transformer' | 'serde' = 'serde';

  public clients: ClientFindInterface[] = [];
  public users: UserFindInterface[] = [];
  public userGroups: UserGroupFindInterface[] = [];
  public taskTypes: HelpdeskTaskTypeFindInterface[] = [];
  public templates: HelpdeskTaskTemplateFindInterface[] = [];
  public createdTasks: HelpdeskTaskFindInterface[] = [];
  public createdTasksLoading: boolean;
  public periodicity: TemplateEntityPeriodicityEnum;
  public today = moment().format('YYYY-MM-DD');

  public get TemplateEntityPeriodicityEnum() {
    return TemplateEntityPeriodicityEnum;
  }

  public externalUsers: ExternalUserFindInterface[] = [];
  public priorities = Object.keys(HelpdeskTaskPriorityEnum);

  private formSubscription$: Subscription = new Subscription();

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly helpDeskTaskService: HelpDeskTaskService,
    private readonly clientService: ClientService,
    private readonly userService: UserService,
    private readonly userGroupService: UserGroupService,
    private readonly helpDeskTaskTypeService: HelpDeskTaskTypeService,
    private readonly helpDeskTaskTemplateService: HelpDeskTaskTemplateService,
    private readonly cdRef: ChangeDetectorRef,
    public readonly helpDeskSettingsService: HelpDeskSettingsService,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService,
    private readonly router: Router
  ) {
    super();
  }

  public ngOnInit() {
    const taskId = this.formStoreService.getState.forms[this.index]?.data?.taskId;

    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (!isNaN(taskId)) {
      this.helpDeskTaskService.findById<HelpdeskTaskFindInterface>(taskId).subscribe((task) => {
        this.helpdeskTemplate = {
          ...this.helpdeskTemplate,
          client: task.client,
          externalUser: task.externalUser,
          project: task.project,
          deal: task.deal,
          responsible: task.responsible,
          taskType: task.taskType,
          unit: task.unit,
          description: task.description,
          userAuditors: task.userAuditors,
          userGroupAuditors: task.userGroupAuditors,
          plannedMin: task.plannedMin,
          planning: {
            periodicity: TemplateEntityPeriodicityEnum.NONE
          },
          deadline: {
            daysAfter: 0,
            type: DeadlineTypeEnum.RELATIVE
          },
          masterMilestone: task.masterMilestone
        };
        this.cdRef.markForCheck();
      });
    }

    forkJoin([
      this.clientService.findAll({ select: ['id', 'name', 'contragents'] }),
      this.userService.findAll(),
      this.userGroupService.find<UserGroupFindInterface>(),
      this.helpDeskTaskTypeService.find<HelpdeskTaskTypeFindInterface>()
    ]).subscribe(([clients, users, userGroups, directions]) => {
      this.clients = clients;
      this.users = users;
      this.userGroups = userGroups;
      this.taskTypes = directions;
      this.cdRef.markForCheck();
    });

    forkJoin([this.helpDeskTaskTemplateService.findAll(), this.helpDeskSettingsService.getDefaultClient()]).subscribe(
      ([templates, defaultClient]) => {
        this.templates = templates;
        if (!this.helpdeskTemplate.client && defaultClient) {
          this.helpdeskTemplate.client = defaultClient;
        }
        if (this.helpdeskTemplate?.client?.externalUsers) {
          this.externalUsers = this.helpdeskTemplate.client.externalUsers;
        }
        this.cdRef.markForCheck();
      }
    );

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    } else {
      this.helpdeskTemplate.planning = <PlanningUnitFormInterface>{};
      this.helpdeskTemplate.planning.periodicity = TemplateEntityPeriodicityEnum.NONE;

      this.helpdeskTemplate.deadline = <DeadlineFormInterface>{};
      this.helpdeskTemplate.deadline.daysAfter = 0;
      this.helpdeskTemplate.deadline.type = DeadlineTypeEnum.RELATIVE;
    }
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.helpdeskTemplate?.currentValue) {
      const helpdeskTemplate = changes.helpdeskTemplate?.currentValue as HelpdeskTaskTemplateFindInterface;
      this.changeClient(helpdeskTemplate.client);
    }
  }

  public ngOnDestroy() {
    this.formSubscription$?.unsubscribe();
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.helpDeskTaskTemplateService
      .findById<HelpdeskTaskTemplateFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .pipe(
        tap((helpdeskTemplate) => {
          this.helpdeskTemplate = helpdeskTemplate;
          this.periodicity = helpdeskTemplate.planning.periodicity;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }),
        switchMap((helpdeskTemplate: HelpdeskTaskTemplateFindInterface) =>
          helpdeskTemplate.planning.periodicity !== TemplateEntityPeriodicityEnum.NONE
            ? this.helpDeskTaskTemplateService.getCreatedTasksWidget(
                this.formStoreService.getState.forms[this.index]?.entityId
              )
            : of([])
        )
      )
      .subscribe({
        next: (tasks) => {
          this.createdTasks = tasks;
          this.createdTasksLoading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.createdTasksLoading = false;
          this.cdRef.markForCheck();
        }
      });
  }

  public saveTemplate() {
    if (this.checkValidate(this.helpdeskTemplate).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.helpDeskTaskTemplateService
          .update<HelpdeskTaskTemplateFormInterface, HelpdeskTaskTemplateFindInterface>(this.helpdeskTemplate)
          .subscribe({
            next: (template) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Шаблон "${template.name}" обновлен`);
              this.formStoreService.saveNext(template, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.helpDeskTaskTemplateService.create<HelpdeskTaskFindInterface>(this.helpdeskTemplate).subscribe({
          next: (template) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Шаблон создан');
            this.formStoreService.saveNext(template, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteTemplate() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить шаблон "${this.helpdeskTemplate.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.helpDeskTaskTemplateService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe(() => {
            this.errorService.addSuccessMessage(`Шаблон "${this.helpdeskTemplate.name}" удален`);
            this.formStoreService.deleteNext(this.index);
          });
      }
    });
  }

  public changeClient(client: ClientFindInterface) {
    if (client) {
      this.externalUsers = client.externalUsers;
    } else {
      this.externalUsers = [];
      this.cdRef.markForCheck();
    }
  }

  public openFormCreateExternalUser() {
    const result = this.formStoreService.addForm(TypeFormEnum.EXTERNAL_USER_CREATE, {
      headerText: 'Добавление внешнего пользователя',
      widthSize: 'form-sidebar-small',
      data: {
        client: this.helpdeskTemplate?.client,
        contragents: this.helpdeskTemplate?.client?.contragents
      }
    });

    this.formSubscription$.add(
      result.onSave.subscribe((externalUser: ExternalUserFindInterface) => {
        if (externalUser) {
          this.externalUsers.push(externalUser);
          this.externalUsers = [...this.externalUsers];
          this.helpdeskTemplate.externalUser = externalUser;
          this.cdRef.markForCheck();
        }
      })
    );
  }

  public directToTasksList() {
    const queryParams = {
      from: moment().subtract(1, 'year').format('YYYY-MM-DD'),
      to: moment().format('YYYY-MM-DD'),
      templateIds: [this.formStoreService.getState.forms[this.index]?.entityId]
    };
    const url = String(this.router.createUrlTree(['/helpdesk/task'], { queryParams }));
    window.open(url, '_blank');
  }
}
